/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect } from "react";
import { Else, If, Then } from "react-if";
import {
  Box,
  BrickLoader,
  FlexContainer,
  Modal,
  Spacer,
  theme,
} from "@nordcloud/gnui";
import { useGetGroupPlansLazyQuery } from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { showError, showSuccess } from "~/services/toast";
import { inflect } from "~/tools";
import { useDeleteResourceGroup } from "~/views/resourceGroups/hooks";

type Props = {
  groupId: string;
  groupName?: string;
  isDeleteModalOpen: boolean;
  close: () => void;
  onSuccess?: () => void;
};

export function DeleteResourceGroup({
  groupId,
  groupName,
  isDeleteModalOpen,
  close,
  onSuccess,
}: Props) {
  const modalLabel = "Delete Resource Group";

  const [deleteGroup] = useDeleteResourceGroup({
    onSuccess: () => {
      close();
      onSuccess?.();
    },
  });

  function deleteResourceGroup(id: string) {
    deleteGroup(id)
      .then(() => showSuccess(`${groupName} resource group has been removed.`))
      .catch(() => showError());
  }

  function onDelete(id: string) {
    deleteResourceGroup(id);
  }

  const [getPlans, { data, loading }] = useGetGroupPlansLazyQuery({
    variables: { resourceGroupId: groupId },
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (isDeleteModalOpen) {
      getPlans();
    }
  }, [isDeleteModalOpen, getPlans]);

  const groupPlans =
    data?.group?.plans?.map((p) => ({
      id: p?.id,
      name: p?.name,
    })) ?? [];

  const groupPlansCount = groupPlans && groupPlans.length;
  const groupUsedAtPlans = groupPlansCount > 0;

  return (
    <Modal
      onClose={close}
      isOpen={isDeleteModalOpen}
      contentLabel={modalLabel}
      actions={
        groupUsedAtPlans
          ? [
              {
                order: 0,
                onAction: close,
                label: "Cancel",
                severity: "high",
              },
            ]
          : [
              {
                order: 0,
                onAction: () => onDelete(groupId),
                label: "Delete",
                severity: "high",
              },
            ]
      }
    >
      <LoaderWrap loading={loading} Component={BrickLoader}>
        <If condition={groupUsedAtPlans}>
          <Then>
            {`You are trying to delete ${groupName}, but this resource group is used in the ${inflect(
              "plan"
            )(groupPlansCount)} below:`}
            <Spacer height={theme.spacing.spacing04} />
            {groupPlans?.map((planDetail) => (
              <FlexContainer
                key={planDetail.id}
                direction="row"
                justifyContent="space-around"
              >
                <Box boxStyle="lightGrey"> {planDetail.name} </Box>
              </FlexContainer>
            ))}
            <Spacer height={theme.spacing.spacing04} />
            {`If you still want to delete this resource group, you must delete it from the ${inflect(
              "plan"
            )(groupPlansCount)} first.`}
          </Then>
          <Else>{`Are you sure you want to delete ${groupName} resource group?`}</Else>
        </If>
      </LoaderWrap>
    </Modal>
  );
}
